import { Download, CheckCircle2, ArrowUpRight, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { Reveal, scrollToId } from "@/components/site/motion";
import { NCC_REQUIREMENTS, NCC_FOA, NCC_STEPS, NCC_SIZING, NCC_GUIDE } from "@/components/site/data";
import { Seo, SITE } from "@/components/site/Seo";

const nccLd = {
  "@context": "https://schema.org",
  "@type": "TechArticle",
  headline: "SolarArk & NCC 2022 Clause 10.8.3 — Roof Space Ventilation",
  description: "How SolarArk solar roof ventilators meet and exceed NCC 2022 Deemed-to-Satisfy provisions for roof-space ventilation and condensation management.",
  author: { "@type": "Organization", name: "SolarArk Australia" },
  publisher: { "@type": "Organization", name: "SolarArk Australia", logo: { "@type": "ImageObject", url: `${SITE.base}/solarark-white.png` } },
};

const Section = ({ children, className = "", ...rest }) => (
  <section className={`relative py-20 lg:py-28 ${className}`} {...rest}>
    <div className="max-w-7xl mx-auto px-6 lg:px-12">{children}</div>
  </section>
);

export default function NccPage() {
  return (
    <>
      <Seo
        path="/ncc-compliance"
        title="NCC 2022 Clause 10.8.3 Compliance"
        description="SolarArk solar roof ventilators are a performance solution that meets and exceeds the NCC 2022 Deemed-to-Satisfy provisions for roof-space ventilation (Clause 10.8.3) and condensation management."
        jsonLd={nccLd}
      />
      <PageHeader
        overline="NCC 2022 Compliance · Clause 10.8.3"
        title="NCC 10.8.3, solved."
        subtitle="SolarArk solar roof ventilators are a performance solution that meets and exceeds the Deemed-to-Satisfy provisions for roof-space ventilation and condensation management under NCC 2022."
      />

      {/* Context */}
      <Section data-testid="ncc-context" className="bg-[#050505] border-y border-[#1c1c1c]">
        <div className="grid lg:grid-cols-[0.9fr_1.6fr] gap-16 lg:gap-24">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-6">Condensation Management</p>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
                Why 10.8.3 exists.
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed max-w-sm">
                Modern airtight, thermally-efficient homes trap more moisture. In roof spaces, heat, humidity and poor air movement create serious durability, energy-efficiency and air-quality risks.
              </p>
            </Reveal>
          </div>
          <div className="space-y-4">
            {[
              { t: "A performance solution", d: "When correctly specified and installed, SolarArk ventilators deliver sustained ventilation rates exceeding passive DTS methods — satisfying NCC 2022 Vol. 1 F8D5 and ABCB Housing Provisions 10.8.3." },
              { t: "Especially Climate Zones 6, 7 & 8", d: "Active convective air exchange reduces moisture retention, thermal stratification and heat-load accumulation in the roof cavity — improving envelope integrity and energy efficiency." },
              { t: "Unobstructed Free Open Area", d: "FOA is the actual area air moves through. SolarArk units measure FOA at a completely unobstructed base opening — where a 400×200 mm eave vent (80,000 mm² gross) may only deliver 15,000–20,000 mm² effective, a SAV-20 delivers 196,349 mm²." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06}>
                <div className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-7 lg:p-8 flex gap-5">
                  <ShieldCheck className="shrink-0 text-[#fcc41b] mt-1" size={24} />
                  <div>
                    <h3 className="font-display font-semibold text-xl text-white mb-2">{c.t}</h3>
                    <p className="text-zinc-400 leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* Requirements table */}
      <Section data-testid="ncc-requirements" className="bg-[#010101]">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">The Requirement</p>
          <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white mb-4" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
            NCC 10.8.3 roof-space ventilation.
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-10">Minimum free open area per metre of the roof's longest horizontal dimension, by roof pitch.</p>
        </Reveal>
        <Reveal>
          <div className="overflow-x-auto rounded-3xl border border-[#262626]">
            <table className="w-full text-left border-collapse min-w-[640px]" data-testid="ncc-req-table">
              <thead>
                <tr className="bg-[#0d0d0d] text-zinc-500 font-mono-eng text-[11px] uppercase tracking-wider">
                  <th className="py-4 px-6 font-medium">Roof Pitch</th>
                  <th className="py-4 px-6 font-medium">Required Ventilation Openings</th>
                </tr>
              </thead>
              <tbody>
                {NCC_REQUIREMENTS.map((r) => (
                  <tr key={r.pitch} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d] transition-colors duration-200">
                    <td className="py-5 px-6 font-display font-semibold text-[#fcc41b] whitespace-nowrap">{r.pitch}</td>
                    <td className="py-5 px-6 text-zinc-300 leading-relaxed">{r.vent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-zinc-500 text-sm">High-level openings are provided at the ridge, or no more than 900 mm below the highest point of the roof space.</p>
        </Reveal>
      </Section>

      {/* FOA table */}
      <Section data-testid="ncc-foa" className="bg-[#050505] border-y border-[#1c1c1c]">
        <Reveal>
          <h2 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight mb-8">SolarArk ventilator free open areas</h2>
        </Reveal>
        <Reveal>
          <div className="overflow-x-auto rounded-3xl border border-[#262626]">
            <table className="w-full text-left border-collapse min-w-[520px]" data-testid="ncc-foa-table">
              <thead>
                <tr className="bg-[#0d0d0d] text-zinc-500 font-mono-eng text-[11px] uppercase tracking-wider">
                  <th className="py-4 px-6 font-medium">Model</th>
                  <th className="py-4 px-6 font-medium">Free Open Area</th>
                  <th className="py-4 px-6 font-medium">mm²</th>
                </tr>
              </thead>
              <tbody>
                {NCC_FOA.map((r) => (
                  <tr key={r.model} className="border-t border-[#1c1c1c] hover:bg-[#0d0d0d] transition-colors duration-200">
                    <td className="py-4 px-6 font-display font-semibold text-white whitespace-nowrap">{r.model}</td>
                    <td className="py-4 px-6 font-mono-eng text-[#fcc41b]">{r.area}</td>
                    <td className="py-4 px-6 font-mono-eng text-zinc-400">{r.mm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </Section>

      {/* Worked example */}
      <Section data-testid="ncc-example" className="bg-[#010101]">
        <Reveal>
          <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Worked Example</p>
          <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white mb-4" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
            A 20 × 10 m roof at 20° pitch.
          </h2>
          <p className="text-zinc-400 max-w-2xl leading-relaxed mb-12">How a single SAV-20/20T plus eave vents achieves full compliance.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-4">
          {NCC_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 h-full flex flex-col">
                <span className="font-display font-light text-5xl text-[#fcc41b]/80 leading-none mb-6">{s.n}</span>
                <h3 className="font-display font-semibold text-xl text-white mb-4">{s.title}</h3>
                <p className="font-mono-eng text-xs text-zinc-500 leading-relaxed mb-4">{s.req}</p>
                <div className="mt-auto flex items-start gap-2 text-zinc-300 leading-relaxed">
                  <CheckCircle2 size={18} className="shrink-0 text-[#fcc41b] mt-1" />
                  <span>{s.sol}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {NCC_SIZING.map((r) => (
              <div key={r.model} className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-7 flex items-center gap-5">
                <span className="font-display font-semibold text-lg text-[#fcc41b] whitespace-nowrap">{r.model}</span>
                <span className="text-zinc-400">{r.rule}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-zinc-500 text-sm max-w-2xl">
            Install the ventilator on the north-facing pitch where possible; use a tilt-panel "T" model for east, south or west-facing roofs. Eave intake must at least equal the ventilator opening area.
          </p>
        </Reveal>

        {/* CTA */}
        <Reveal>
          <div className="mt-16 rounded-3xl border border-[#fcc41b]/30 bg-[#0d0d0d] p-8 lg:p-12 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white mb-2">Specifying for a project?</h3>
              <p className="text-zinc-400 leading-relaxed max-w-xl">Download the full NCC 10.8.3 guide, or have our engineers size a compliant solution for your roof.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a href={NCC_GUIDE} target="_blank" rel="noopener noreferrer" data-testid="ncc-download-btn" className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-7 py-4 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300">
                Download NCC Guide <Download size={18} />
              </a>
              <button onClick={() => scrollToId("quote")} data-testid="ncc-quote-btn" className="group inline-flex items-center justify-center gap-3 rounded-full border border-[#fcc41b]/60 text-[#fcc41b] font-display font-medium px-7 py-4 hover:bg-[#fcc41b]/10 transition-colors duration-300">
                Ask our engineers <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </Reveal>
      </Section>

      <QuoteFooter blurb="Building or specifying? Tell us your roof dimensions and pitch and our engineers will size an NCC 10.8.3-compliant ventilation solution — no obligation." />
    </>
  );
}
